import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useMutation } from 'react-apollo';
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { displayError } from '../utilities';
import { UPSERT_CATEGORY } from '../queries';
import FieldErrorMessage from './FieldErrorMessage';

const StyledFormRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: flex-start;
  margin-bottom: 18px;
`;

const StyledLabel = styled.label`
  margin-top: 8px;
  font-weight: 500;
`;

const StyledPreviewImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  border: 1px solid #ddd;
  padding: 5px;
  border-radius: 4px;
`;

const AddEditCategoryModal = ({
  isOpen,
  toggle,
  isEdit,
  category,
  onSuccess
}) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (isOpen && category) {
      setPreviewUrl(category.iconLink || '');
    } else {
      setPreviewUrl('');
    }
  }, [category, isOpen]);

  const [upsertCategory] = useMutation(UPSERT_CATEGORY);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Category name is required')
      .min(3, 'Category name must be at least 3 characters')
      .max(50, 'Category name cannot exceed 50 characters')
      .matches(
        /^[a-zA-Z0-9\s&-]+$/,
        'Only alphanumeric characters, & and - are allowed'
      )
      .test(
        'not-only-special',
        'Category name must contain at least one letter or number',
        value => /[a-zA-Z0-9]/.test(value || '')
      ),
    iconFile: Yup.mixed()
      .test('required', 'Category icon is required', function(value) {
        if (!isEdit && !value) return false;
        return true;
      })
      .test('fileSize', 'Image size is too large (max 200KB)', value => {
        if (!value || typeof value === 'string') return true;
        return value.size <= 200 * 1024;
      })
      .test('aspectRatio', 'Image must be 1:1 aspect ratio', async value => {
        if (!value || typeof value === 'string') return true;
        return new Promise(resolve => {
          const reader = new FileReader();
          reader.readAsDataURL(value);
          reader.onload = e => {
            const img = new Image();
            img.src = e.target.result;
            img.onload = () => {
              resolve(img.width === img.height);
            };
            img.onerror = () => resolve(false);
          };
          reader.onerror = () => resolve(false);
        });
      })
  });

  const handleSave = async (values, { setSubmitting }) => {
    try {
      await upsertCategory({
        variables: {
          id: isEdit ? category.id : null,
          name: values.name.trim(),
          iconFile: values.iconFile || null
        }
      });

      toast.success(
        `${isEdit ? 'Updated' : 'Added'} category ${values.name} successfully`,
        { autoClose: 3000 }
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (e) {
      displayError(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {isEdit ? 'Edit Category' : 'Add Category'}
      </ModalHeader>

      <Formik
        initialValues={{
          name: category ? category.name : '',
          iconFile: null
        }}
        validationSchema={validationSchema}
        onSubmit={handleSave}
        enableReinitialize
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          isValid,
          dirty
        }) => (
          <Form>
            <ModalBody>
              <div className="mapping-form">
                <StyledFormRow>
                  <StyledLabel>Category Name</StyledLabel>
                  <div className="flex-grow-1">
                    <input
                      name="name"
                      type="text"
                      className="form-control"
                      value={values.name}
                      onChange={e => {
                        setFieldValue('name', e.target.value);
                        setFieldTouched('name', true, false);
                      }}
                      placeholder="Enter category name"
                    />
                    <FieldErrorMessage name="name" />
                  </div>
                </StyledFormRow>

                <StyledFormRow>
                  <StyledLabel>Category Icon</StyledLabel>
                  <div className="icon-upload-section flex-grow-1">
                    {previewUrl && (
                      <div className="icon-preview-wrapper mb-3">
                        <StyledPreviewImage
                          src={previewUrl}
                          alt="Preview"
                          className="category-icon-preview"
                        />
                      </div>
                    )}

                    <input
                      name="iconFile"
                      type="file"
                      className="form-control-file"
                      accept="image/*"
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file) {
                          setFieldValue('iconFile', file);
                          setFieldTouched('iconFile', true, false);
                          setPreviewUrl(URL.createObjectURL(file));
                        }
                      }}
                    />
                    <FieldErrorMessage name="iconFile" />
                  </div>
                </StyledFormRow>
              </div>
            </ModalBody>

            <ModalFooter className="mapping-modal-footer">
              <Button
                type="submit"
                className="mapping-save-btn"
                disabled={
                  isSubmitting ||
                  !isValid ||
                  (isEdit ? !dirty : !values.iconFile)
                }
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>

              <Button className="mapping-cancel-btn" onClick={toggle}>
                Cancel
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

AddEditCategoryModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  isEdit: PropTypes.bool,
  category: PropTypes.object,
  onSuccess: PropTypes.func
};

export default AddEditCategoryModal;
